import React, { useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { ImageOrVid } from './ui/ImageOrVid';

interface Props {
    mediaUrls: string[];
    useThumbnail?: boolean;
}

const RentalMediaCarousel: React.FC<Props> = ({ mediaUrls, useThumbnail }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [index, setIndex] = useState(0);

    const scrollToIndex = (i: number) => {
        const container = containerRef.current;
        if (!container) return;
        const child = container.children[i] as HTMLElement;
        child?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest', });
        setIndex(i);
    };

    const next = () => {
        if (index < mediaUrls.length - 1) scrollToIndex(index + 1);
    };

    const prev = () => {
        if (index > 0) scrollToIndex(index - 1);
    };

    const handleScroll = () => {
        const container = containerRef.current;
        if (!container) return;
        const { scrollLeft, offsetWidth } = container;
        const i = Math.round(scrollLeft / offsetWidth);
        setIndex(i);
    };

    return (
        <div className="relative">
            {/* Scrollable container */}
            <div
                ref={containerRef}
                onScroll={handleScroll}
                className="flex overflow-x-auto scroll-smooth snap-x snap-mandatory gap-2 rounded-xl"
            >
                {mediaUrls.map((_, i) => (
                    <div
                        key={i}
                        className="snap-start flex-shrink-0 w-full h-full relative rounded-xl overflow-hidden"
                        style={{ minWidth: '100%' }}
                    >
                        <ImageOrVid mediaUrls={mediaUrls} index={i} isCard onClick={() => scrollToIndex(i)} useThumbnail={useThumbnail} />
                    </div>
                ))}
            </div>

            {/* Arrows */}
            {mediaUrls.length > 1 && (
                <>
                    <button
                        onClick={(e) => {
                            e.preventDefault(); 
                            e.stopPropagation();
                            prev();
                        }}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-700 p-1 rounded-full shadow"
                    >
                        <ChevronLeftIcon className="w-5 h-5" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.preventDefault(); 
                            e.stopPropagation();
                            next();
                        }}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-700 p-1 rounded-full shadow"
                    >
                        <ChevronRightIcon className="w-5 h-5" />
                    </button>
                </>
            )}

            {/* Indicator */}
            {mediaUrls.length > 0 && (
                <div className="absolute bottom-2 right-2 bg-white/80 text-sm text-gray-700 px-2 py-1 rounded shadow">
                    {index + 1} / {mediaUrls.length}
                </div>
            )}
        </div>
    );
};

export default RentalMediaCarousel;
